/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Heading, HeadingLevel, Icon, Link, Text } from '@zapier/design-system';
import { Fragment, ReactNode } from 'react';

import { ACCOUNT_USAGE_LINK } from './constants';
import { Divider } from '../Divider';
import InAppSidebarError from './InAppSidebarError';
import PlanUsage from './PlanUsage/PlanUsage';
import UniversalNavLink from '../../UniversalNavLink';
import { isLinkSelected } from './utils';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  useQueryAccount,
  useQueryAccountUsage,
  useUniversalNavContext,
} from '../../../hooks';
import { useQueryAccountPlan } from '../../../../universal-layout/hooks';

import type { InAppSidebarProps } from './InAppSidebar';

const infinitize = (number: number) => {
  return number === -1 ? Infinity : number;
};

const Styles = labelStyles('InAppSidebarFooter', {
  footerWrapper: {
    overflow: 'hidden',
    padding: '6px var(--zds-space-8, 8px)',
  },
  tOSWrapper: {
    whiteSpace: 'initial',
    background: 'var(--zds-gray-warm-2, #F5F3EB)',
    border: `1px solid var(--zds-gray-warm-3, #ECE9DF)`,
    borderRadius: 10,
    padding: 15,
  },
  tOSHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  tOSHeading: {
    display: 'flex',
    alignItems: 'center',
  },
  tOSDismissalIconButton: {
    display: 'flex',
    alignItems: 'center',
    '&:focus': {
      outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
    },
  },
  tOSDismissalButton: {
    color: `var(--zds-ui-primary-strongest, #503EBD)`,
    textDecoration: 'underline',
    '&:focus': {
      outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
    },
  },
});

const FooterWrapper = ({ children }: { children: ReactNode }) => (
  <div css={Styles.footerWrapper} data-zds>
    {children}
  </div>
);

const MaybeTOS = () => {
  const LATEST_VERSION_OF_TERMS = 'design-system-tos-notification-2022-11-15';
  const hasUserDismissedLatestToS =
    localStorage.getItem('versionOfTermsDismissed') ===
      LATEST_VERSION_OF_TERMS || false;

  const handleDismiss = () => {
    // Wrap localStorage write in try/catch in case browser memory quota is exceeded
    try {
      localStorage.setItem('versionOfTermsDismissed', LATEST_VERSION_OF_TERMS);
    } catch {
      console.log('Failed to save versionOfTermsDismissed to localStorage');
    }
  };

  const { getFullyQualifiedURL, shouldRenderTOS } = useUniversalNavContext();
  return shouldRenderTOS && hasUserDismissedLatestToS === false ? (
    <Fragment>
      <Divider />
      <div css={Styles.tOSWrapper}>
        <div css={Styles.tOSHeader}>
          <div css={Styles.tOSHeading}>
            <Icon
              ariaHidden={true}
              color="blueJeans"
              name="alertInfoFill"
              size={18}
            />
            <HeadingLevel>
              <Text margin="0 0 0 5px" tag={Heading} type="smallPrint1Semibold">
                Updated Terms
              </Text>
            </HeadingLevel>
          </div>
          <button
            aria-label="Dismiss notification"
            css={Styles.tOSDismissalIconButton}
            onClick={handleDismiss}
            type="button"
            data-zds
          >
            <Icon ariaHidden={true} color="neutral800" name="formX" size={24} />
          </button>
        </div>
        <Text tag="p" type="smallPrint1">
          We updated the{' '}
          <Link href={getFullyQualifiedURL('/legal/terms-of-service')}>
            Zapier Terms of Service
          </Link>{' '}
          on November 15, 2022. For existing users, these updated terms will be
          effective beginning on December 15, 2022. By continuing to use Zapier,
          you agree to these updated terms.{' '}
          <button
            css={Styles.tOSDismissalButton}
            type="button"
            onClick={handleDismiss}
            data-zds
          >
            Dismiss
          </button>
        </Text>
      </div>
    </Fragment>
  ) : null;
};

type FooterProps = Pick<
  InAppSidebarProps,
  'currentAccountId' | 'isCollapsed' | 'selectedHref'
>;

const Footer = ({
  currentAccountId,
  isCollapsed,
  selectedHref,
}: FooterProps) => {
  const { data: accountData, isError: isAccountError } = useQueryAccount({
    currentAccountId,
  });
  const { data: accountUsageData, isError: isAccountUsageError } =
    useQueryAccountUsage(currentAccountId);
  const { data: accountPlanData, isError: isAccountPlanError } =
    useQueryAccountPlan(currentAccountId, accountData?.plan?.id);

  const renderedUsageLink = (
    <UniversalNavLink
      href={ACCOUNT_USAGE_LINK.href}
      icon={
        typeof ACCOUNT_USAGE_LINK.icon === 'object'
          ? ACCOUNT_USAGE_LINK.icon.selected
          : ACCOUNT_USAGE_LINK.icon
      }
      isCollapsed={isCollapsed}
      isSelected={isLinkSelected(ACCOUNT_USAGE_LINK.href || '', selectedHref)}
      isSidebar={true}
      text={ACCOUNT_USAGE_LINK.text}
    />
  );

  if (!accountData) return null;

  if (isCollapsed) {
    return null;
  }

  // TODO: investigate if we really want to render this error message
  if (isAccountError || isAccountPlanError || isAccountUsageError) {
    return (
      <Fragment>
        {renderedUsageLink}
        <FooterWrapper>
          <InAppSidebarError>An error has occurred</InAppSidebarError>
          <MaybeTOS />
        </FooterWrapper>
      </Fragment>
    );
  }

  if (!accountUsageData || !accountPlanData) {
    return null;
  }

  const {
    plan,
    isTrialing,
    trialEndDate,
    isTrialingTeams,
    teamTrialEndDate,
    isTrialingCompanies,
    companiesTrialEndDate,
    isTrialingEnterprise,
    enterpriseTrialEndDate,
  } = accountData;

  const { meteredPricingTiers: tiers } = accountPlanData;

  const freeTier = tiers && tiers.find((tier) => tier.pricePerTask === 0);
  const numFreeTasksMax = freeTier ? freeTier.numTasks : 0;
  const normalizedUsage = {
    isPlanMetered: plan.hasMeteredBilling,
    numTasks: accountUsageData.tasks.total,
    numFreeTasksMax,
    numTasksMax: infinitize(accountUsageData.tasks.limit),
    numZaps: accountUsageData.zaps.on,
    numZapsMax: infinitize(accountUsageData.zaps.limit),
  };

  const trialPlanName = isTrialingTeams
    ? 'Team'
    : isTrialingCompanies
    ? 'Company'
    : isTrialingEnterprise
    ? 'Enterprise'
    : isTrialing
    ? 'Professional'
    : undefined;
  const trialPlanEndDate = isTrialingTeams
    ? teamTrialEndDate
    : isTrialingCompanies
    ? companiesTrialEndDate
    : isTrialingEnterprise
    ? enterpriseTrialEndDate
    : trialEndDate;

  return (
    <FooterWrapper>
      <PlanUsage
        {...normalizedUsage}
        resetDate={accountUsageData.periodEnd}
        title={`${plan.name} Plan`}
        trialPlanName={trialPlanName}
        trialPlanEndDate={trialPlanEndDate ?? undefined}
      >
        <MaybeTOS />
      </PlanUsage>
    </FooterWrapper>
  );
};

export default Footer;
